import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {FORM_ID_CONSTANTS} from './FormUtil';
import './FormHelpBlock.less';
import {InlineMessage} from '@homeaway/react-alerts';

// eslint-disable-next-line complexity
function FormHelpBlock({
    id,
    errorMsg,
    message,
    minChars,
    maxChars,
    disabled,
    charCountDisplay,
    maxCharCountExceeded,
    minCharCountMeet,
    hasMinCountError,
    minCharMessage,
    maxCharMessage,
    inverse
}) {
    // Only render Help block is there is a message, helpText or a character count condition
    const helpBlockIsNeeded = (typeof message !== 'undefined' || message || maxChars > 0 || minChars > 0) ? true : false;

    if (helpBlockIsNeeded) {
        const helpBlockClasses = classNames('FormHelpBlock', 'help-block', {});
        // If maximum char count is exceeded add .FormHelpBlock__max-chars--exceeded it displays char count in bold
        const charCountClasses = classNames('FormHelpBlock__max-chars', {
            'FormHelpBlock__max-chars--exceeded': maxCharCountExceeded,
            'FormHelpBlock__max-chars--not-exceeded': !maxCharCountExceeded
        });
        // errorMsg is given priority over helpText (similar to FormInput)
        const messageType = errorMsg ? 'danger' : 'help';

        let minCountMessageType = 'help';

        // Determine message type to be shown for min char condition
        if (hasMinCountError && minCharCountMeet || hasMinCountError) {
            minCountMessageType = 'danger';
        } else if (minCharCountMeet && !hasMinCountError) {
            minCountMessageType = 'success';
        }

        return (
            <div className={helpBlockClasses}>
                <div className="FormHelpBlock__messages">
                    {minChars > 0 &&
                        <InlineMessage animateIfJustMessageChanges={false} inverse={inverse} id={id + FORM_ID_CONSTANTS.MIN_CHAR} type={minCountMessageType} message={minCharMessage} marginTop={4} />
                    }
                    {maxCharCountExceeded &&
                        <InlineMessage animateIfJustMessageChanges={false} inverse={inverse} id={id + FORM_ID_CONSTANTS.MAX_CHAR} type="danger" message={maxCharMessage} marginTop={4} />
                    }
                    {message &&
                        <InlineMessage animateIfJustMessageChanges={false} inverse={inverse} id={id + FORM_ID_CONSTANTS.HELP_TEXT} type={messageType} message={message} marginTop={4} />
                    }
                </div>
                {maxChars > 0 && !disabled &&
                    <div className={charCountClasses}>{charCountDisplay}</div> // DOM to be rendered for the character count shown on the right
                }
            </div>
        );
    }
    return null;
}

//  All of the props are assigned a value here from FormUtil
FormHelpBlock.propTypes = {
    /** Id referring to the input field for which the help block is used for */
    id: PropTypes.string.isRequired,
    /** When populated with a string, this will display a help message beneath the input */
    helpText: PropTypes.node,
    /** When populated with a string, this will turn the border red and display a validation error beneath the input. */
    errorMsg: PropTypes.node,
    /** Contains value of text to be displayed below the input or textarea. It is equal to the value of errorMsg if both helpText and errorMsg is passed.*/
    message: PropTypes.node,
    /** Minimum characters required value */
    minChars: PropTypes.number,
    /** Maximum characters required value */
    maxChars: PropTypes.number,
    /** Flag to disable the help block */
    disabled: PropTypes.bool,
    /** Maximum char count value, displayed on the right side of form */
    charCountDisplay: PropTypes.number,
    /** Flag which indicates if the maximum char count requirement is exceeded */
    maxCharCountExceeded: PropTypes.bool,
    /** Flag indicating if the minimum char count condition has been met */
    minCharCountMeet: PropTypes.bool,
    /** Flag indicating if the error is due to minimum char count not met */
    hasMinCountError: PropTypes.bool,
    /** Message to be displayed for minimum char count error */
    minCharMessage: PropTypes.string,
    /** Message to be displayed for maximum char count error */
    maxCharMessage: PropTypes.string,
    /** Flag when true indicates that the messages need to rendered in inverse color (white) */
    inverse: PropTypes.bool
};

FormHelpBlock.defaultProps = {
    maxChars: 0,
    minChars: 0,
    inverse: false
};

export default FormHelpBlock;
